import { ReplaySubject } from 'rxjs';
import { CouversationSource } from 'shared/interfaces/Conversation';
import { Memory } from 'shared/interfaces/Memory';
import { CompleteEmailHandoffToolConfig } from 'shared/interfaces/tools/CompleteEmailHandoff';

export interface ChatMessage {
  text: string;
  fromUser: boolean;
  partialResponseSubject?: ReplaySubject<SmartAssistantMessageEvent>;
  toolEvent?: SmartAssistantToolEvent;
  toolInvocation?: SmartAssistantToolInvocation;
  secondsFromStart?: number;
  labels?: ChatMessageLabel[];
  attachments?: ChatAttachment[];
}

export enum ChatMessageLabel {
  FOLLOW_UP = 'followUp'
}

export enum ChatAttachmentType {
  IMAGE = 'image',
  AUDIO = 'audio'
}

export interface ChatAttachment {
  _id: string;
  type: ChatAttachmentType;
  bucketUrl: string;
  displayUrl: string;
  description?: string;
}

export interface AttachmentUploadUrl {
  uploadUrl: string;
  attachmentId: string;
}

export interface TranscribedAudioAttachment {
  transcription: string;
}

export interface AskAboutResourceDto {
  conversation: ChatMessage[];
  resourceId: string;
}

export interface AskSmartAssistantDto {
  smartAssistantId: string;
  conversationInfo: AskSmartAssistantConversationInfoDto;
  forcedToolInvocation?: ForcedToolInvocation;
  overrideMemories?: Memory[];
}

export interface AskDto {
  conversationInfo: AskSmartAssistantConversationInfoDto;
  partialResponseSubject: ReplaySubject<SmartAssistantMessageEvent>;
  smartAssistantId?: string;
  resourceId?: string;
  forcedToolInvocation?: ForcedToolInvocation;
  isNewConversation?: boolean;
}

export interface AskSmartAssistantConversationInfoDto {
  conversationId?: string;
  lastMessage?: ChatMessage;
  messages?: ChatMessage[];
  source?: CouversationSource;
  title?: string;
}

export interface SmartAssistantStreamEnd {
  tokensParsed: number;
}

export interface SmartAssistantPartialResponse {
  text: string;
}

export interface SmartAssistantCompleteResponse {
  text: string;
}

export interface SmartAssistantToolEvent {
  hidden?: boolean;
  data?: CalendlyScheduleIntent | ShopifyDisplayProductsToolEventData | EmailHandoffToolEventData; // Add other tool event data
  type: ToolEventType;
}

export enum SourceType {
  PAGE = 'page',
  TIME = 'time',
  NONE = 'none'
}

export interface TextChunkData {
  sourceType: SourceType;
  source: string;
  chunk: string;
  resourceId: string;
  tokenCount: number;
  overlappingTokens: number;
}

export interface SmartAssistantSources {
  relevantTextChunks: TextChunkData[];
}

export interface SmartAssistantMessageEvent {
  data:
    | SmartAssistantPartialResponse
    | SmartAssistantCompleteResponse
    | SmartAssistantStreamEnd
    | SmartAssistantSources
    | SmartAssistantToolEvent;
  id?: string;
  type?: SmartAssistantMessageEventType;
  retry?: number;
  statusCode?: number;
  message?: string;
}

export interface SmartAssistantSyncMessageResponse {
  text: string;
  relevantTextChunks: TextChunkData[];
}

export enum SmartAssistantMessageEventType {
  PARTIAL_RESPONSE = 'partialResponse',
  COMPLETE_RESPONSE = 'completeResponse',
  STREAM_END = 'streamEnd',
  SOURCES = 'sources',
  ERROR = 'error',
  TOOL_EVENT = 'toolEvent'
}

export enum ToolEventType {
  COLLECT_LEAD_INFO = 'collectLeadInfo',
  CALENDLY_TOOL_USER_SHOWED_INTENT_TO_SCHEDULE_MEETING = 'calendlyToolUserShowedIntentToScheduleMeeting',
  SHOPIFY_DISPLAY_PRODUCTS = 'shopifyDisplayProducts',
  SHOPIFY_STORE_KNOWLEDGE = 'shopifyStoreKnowledge',
  EMAIL_HANDOFF = 'emailHandoff',
  COMPLETE_EMAIL_HANDOFF = 'completeEmailHandoff'
}

export interface ForcedToolInvocation {
  config: CompleteEmailHandoffToolConfig; // Add other tool config types here with union type.
}

export interface SmartAssistantToolInvocation {
  toolCall: SmartAssistantToolCall;
  toolResponse: SmartAssistantToolResponse;
}

export interface ToolFunction {
  arguments: string;
  name: string;
}

export interface ToolCallDetails {
  id: string;
  function: ToolFunction;
  type: 'function';
}

export interface SmartAssistantToolCall {
  role: 'assistant';
  content?: string | null;
  tool_calls: ToolCallDetails[];
}

export interface SmartAssistantToolResponse {
  content: string;
  role: 'tool';
  tool_call_id: string;
}

// Data interfaces for tool events
export interface CalendlyScheduleIntent {
  eventUrl: string;
  ctaMessage: string;
}

export interface ShopifyDisplayProductsToolEventData {
  displayedProducts: ShopifyDisplayedProduct[];
}

export interface ShopifyDisplayedProduct {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  collections: string[];
  price: string;
  compareAtPrice?: string;
  url: string;
}

export interface EmailHandoffToolEventData {
  email?: string;
  name?: string;
  phone?: string;
  reason?: string;
}
